/**
 * Resolve a KB file's short id to its full path.
 *
 * Backs deep links of the form `/knowledge/:kbId/f/:fileId`, where only the
 * id lives in the URL. The path is needed to reveal the file in the (lazily
 * loaded) tree and open its preview. Cached per (kbId, fileId) — the mapping
 * is stable until the file is moved/deleted, so a long stale time is fine.
 */

import { useQuery } from '@tanstack/react-query';
import { kbApi } from '../services/api';

export interface KbNodePathResult {
  /** Full path of the file, or `null` while loading / when unresolved. */
  path: string | null;
  isLoading: boolean;
  /** Set when the id can't be resolved (moved/deleted/no access). */
  error: Error | null;
}

export function useKbNodePath(
  kbId: string | null | undefined,
  fileId: string | null | undefined,
): KbNodePathResult {
  const enabled = !!kbId && !!fileId;
  const query = useQuery({
    queryKey: ['kb', kbId, 'node-by-id', fileId],
    queryFn: () => kbApi.statNodeById(kbId as string, fileId as string),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    path: query.data?.path ?? null,
    isLoading: enabled && query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}
